import type {
  MemberGroup,
  MemberGroupMember,
  SortMemberGroupColumn
} from '@library/types/memberGroups';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetMemberGroupMembersQuery,
  useGetMemberGroupsQuery
} from '@bo-services/members/memberGroupsApi';
import { DEFAULT_MEMBER_GROUPS_FILTERS } from '@bo-constants/memberGroups';
import { DEFAULT_MEMBER_GROUP_SORT } from '@library/constants/memberGroups';
import type { MemberGroupModalTypes } from '@bo-hooks/memberGroups/useMemberGroupsContext';
import { delayPromise } from '@bo-utils/common';
import { useFilters } from '@library/hooks/useFilters';
import { usePagination } from '@bo-hooks/usePagination';
import { useSearchParams } from 'react-router-dom';

const MEMBER_GROUP_PARAM_NAME = 'member_group';

export function useMemberGroups() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentMemberGroupId = searchParams.get(MEMBER_GROUP_PARAM_NAME);

  const { filters, setFilter, resetFilters } = useFilters(
    DEFAULT_MEMBER_GROUPS_FILTERS
  );

  const [sortBy, setSortBy] = useState<SortMemberGroupColumn>(
    DEFAULT_MEMBER_GROUP_SORT
  );

  const [currentModal, setCurrentModal] = useState<
    MemberGroupModalTypes | undefined
  >(undefined);
  const [modalMemberGroup, setModalMemberGroup] = useState<
    MemberGroup | undefined
  >(undefined);

  const [isFetchingUpdatedMemberGroups, setIsFetchingUpdatedMemberGroups] =
    useState(false);

  const {
    data: memberGroups,
    isFetching: isFetchingMemberGroups,
    refetch: refetchMemberGroups
  } = useGetMemberGroupsQuery({
    name: filters.name,
    description: filters.description
  });

  const currentMemberGroup = useMemo<MemberGroup | undefined>(() => {
    if (!currentMemberGroupId) return undefined;
    return memberGroups?.find((g) => String(g.id) === currentMemberGroupId);
  }, [currentMemberGroupId, memberGroups]);

  const setCurrentMemberGroup = useCallback(
    (memberGroup: MemberGroup | undefined) => {
      if (memberGroup) {
        searchParams.set(MEMBER_GROUP_PARAM_NAME, String(memberGroup.id));
      } else {
        searchParams.delete(MEMBER_GROUP_PARAM_NAME);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [totalPages, setTotalPages] = useState(1);
  const membersPagination = usePagination(totalPages);

  const {
    data: membersData,
    isFetching: isFetchingMembers,
    refetch: refetchMembers
  } = useGetMemberGroupMembersQuery(
    {
      id: String(currentMemberGroup?.id),
      page: membersPagination.page,
      per_page: 15
    },
    { skip: !currentMemberGroup }
  );

  useEffect(() => {
    if (membersData?.meta.total_pages) {
      setTotalPages(membersData.meta.total_pages);
    }
  }, [membersData?.meta.total_pages]);

  const members = useMemo<MemberGroupMember[] | undefined>(
    () =>
      membersData?.data.map((m) => ({
        ...m.attributes,
        id: m.id
      })),
    [membersData?.data]
  );

  const closeModal = useCallback(() => {
    setCurrentModal(undefined);
    setModalMemberGroup(undefined);
  }, []);

  const openModal = useCallback(
    (modalType: MemberGroupModalTypes, id?: number) => {
      if (typeof id === 'number') {
        const foundMemberGroup = memberGroups?.find((m) => m.id === id);

        if (!foundMemberGroup) {
          closeModal();
          return;
        }

        setModalMemberGroup(foundMemberGroup);
      } else {
        setModalMemberGroup(undefined);
      }

      setCurrentModal(modalType);
    },
    [memberGroups, closeModal]
  );

  const handleMemberGroupsUpdate = useCallback(async () => {
    setIsFetchingUpdatedMemberGroups(true);

    const { promise } = delayPromise(250);
    await promise.then(async () => {
      await refetchMemberGroups();
      // await refetchSelectedUserData();
    });

    setIsFetchingUpdatedMemberGroups(false);
    if (currentModal === 'edit') {
      closeModal();
    }
  }, [refetchMemberGroups, currentModal, closeModal]);

  // TODO: Pull this out in to a type-generic function later so we can use it for more table sorts.
  const sortedMemberGroups = useMemo<typeof memberGroups>(() => {
    if (!memberGroups) return undefined;
    const { sortBy: by, sortDirection: direction } = sortBy;
    if (direction === 'none') return [...memberGroups];

    return [...memberGroups].sort((a, b) => {
      let s = 0;

      if (by === 'name') {
        s = (a.name ?? '').localeCompare(b.name ?? '');
      } else if (by === 'description') {
        s = (a.description ?? '').localeCompare(b.description ?? '');
      } else if (by === 'updated_at') {
        s = new Date(a.updated_at).valueOf() - new Date(b.updated_at).valueOf();
      }

      if (sortBy.sortDirection === 'desc') s = -s;
      return s;
    });
  }, [memberGroups, sortBy]);

  return {
    memberGroups: sortedMemberGroups,
    filters,
    resetFilters,
    setFilter,
    currentModal,
    modalMemberGroup,
    openModal,
    closeModal,
    handleMemberGroupsUpdate,

    currentMemberGroup,
    setCurrentMemberGroup,
    members,
    membersPagination,
    refetchMembers,
    refetchMemberGroups,
    isFetchingMembers,

    isLoading: isFetchingUpdatedMemberGroups ? false : isFetchingMemberGroups,
    setSortBy,
    sortBy
  };
}
